import React, { useState } from "react";
import WidgetCard from "./WidgetCard";
import SparkAreaChart from "./SparkAreaChart";
import { Typography, Row, Col, Skeleton, Popover } from "antd";
import { BsInfoCircleFill } from "react-icons/bs";
import NoDataText from "./NoDataText";
import Delta from "./Delta";
import { keys, map } from "underscore";
import { CommonWidgetMetrics } from "@ts/common-types";
import { formatMetric } from "@ts/revstack/helpers";
import RevstackLaggardsSectionWidget from "./RevstackLaggardsSectionWidget";

const { Text } = Typography;

interface IRevstackSummaryGraphCardProps {
    title: string;
    subTitle?: string;
    infoMessage: string;
    isMetricsLoading?: boolean;
    isGraphLoading?: boolean;
    data: Record<
        CommonWidgetMetrics,
        {
            value: number;
            change: number;
            series: number[];
            isMetricLoading: boolean;
            isGraphLoading: boolean;
        }
    >;
    config: Record<string, any>;
    isDrilldownLoading?: boolean;
    drilldownData?: any[];
    hasFilters?: boolean;
    filterOptions?: { label: string; value: string }[];
}

const RevstackSummaryGraphCard: React.FC<IRevstackSummaryGraphCardProps> = ({
    title,
    subTitle = "\u00A0",
    infoMessage,
    data,
    isMetricsLoading = true,
    isGraphLoading = true,
    isDrilldownLoading = true,
    config,
    drilldownData,
    hasFilters
}) => {

    const defaultFilterOption = config?.defaultFilterOption?.value || "";
    const [selectedFilter, setSelectedFilter] = useState<string>(defaultFilterOption);
    const enableMetricAndGraph = config?.enableMetricAndGraph;

    const isComingSoon = config?.isComingSoon || false;
    const isComingSoonMessage = config?.isComingSoonMessage || "Coming Soon";

    return (
        <WidgetCard className="h-100" style={{ fontWeight: 600, position: 'relative' }} bodyPaddingClass="p-0">

            {enableMetricAndGraph !== false ? <>
                <div className="d-flex flex-column pt-4 px-4">
                    <div className="d-flex align-items-center justify-content-start gap-3">
                        <h5 className="m-0">{title}</h5>
                        <Popover
                            content={
                                <div
                                    className="p-2"
                                    style={{
                                        minWidth: 250,
                                        maxWidth: 300,
                                        fontSize: 12,
                                        whiteSpace: "pre-wrap",
                                        wordWrap: "break-word",
                                    }}
                                >
                                    {infoMessage}
                                </div>
                            }
                            trigger="hover"
                            placement="top"
                        >
                            <BsInfoCircleFill
                                size={12}
                                style={{ color: "#919191" }}
                                role="button"
                            />
                        </Popover>
                    </div>
                    <div className="text-muted" style={{ fontSize: "12px", height: "18px" }}>{subTitle}</div>
                </div>

                {isComingSoon ? <>
                    <NoDataText message={isComingSoonMessage} style={{ padding: "22px 0 40px 0" }} className="d-flex justify-content-center align-items-center"/>
                </> : <>
                    {map(keys(data), (key: string) => {
                        const metricKey = key as CommonWidgetMetrics;
                        const isPositive = data[metricKey].change ? data[metricKey].change >= 0 : null;
                        return (
                            <Row key={`w1-${key}`} align="middle" className={`pt-0 px-4 ${!data[metricKey].isMetricLoading ? "pb-4" : "pb-1"}`}>
                                {(data[metricKey].value === undefined ||
                                    data[metricKey].value === null) &&
                                    !data[metricKey].isMetricLoading ? (
                                    <Col span={24} className="d-flex align-items-center justify-content-center" style={{ fontWeight: 500, minHeight: 80 }}>
                                        <NoDataText />
                                    </Col>
                                ) : (
                                    <>
                                        <Col span={12}>
                                            {isMetricsLoading &&
                                                data[metricKey].isMetricLoading ? (
                                                <Skeleton
                                                    className="w-100 mt-2"
                                                    active
                                                    style={{ height: 70 }}
                                                    loading={
                                                        data[metricKey].isMetricLoading
                                                    }
                                                    paragraph={{ rows: 1, width: 120 }}
                                                />
                                            ) : (
                                                <>
                                                    <Text
                                                        className="fw-semibold fs-4 text-dark"
                                                        style={{
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {formatMetric(
                                                            metricKey,
                                                            data[metricKey].value
                                                        )}
                                                    </Text>
                                                    <Text
                                                        className={`fw-semibold small d-flex align-items-center ${
                                                            isPositive
                                                            ? "text-success"
                                                            : "text-danger"
                                                            }`}
                                                        style={{
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {typeof isPositive === "boolean" && <Delta
                                                            className="me-1"
                                                            isPositive={isPositive}
                                                        />}
                                                        {formatMetric(
                                                            metricKey,
                                                            data[metricKey].change,
                                                            true
                                                        )}
                                                    </Text>
                                                </>
                                            )}
                                        </Col>
                                        <Col span={12}>
                                            {isGraphLoading &&
                                                data[metricKey].isGraphLoading ? (
                                                <Skeleton.Node
                                                    className="w-100"
                                                    style={{
                                                        height: 60,
                                                        borderRadius: 4,
                                                    }}
                                                    active
                                                />
                                            ) : (
                                                <SparkAreaChart
                                                    data={data[metricKey].series}
                                                    height={60}
                                                    width={180}
                                                    tooltipTitle="Performance Trend"
                                                    tooltipValueFormatter={(
                                                        value: number
                                                    ) =>
                                                        `Value: ${formatMetric(
                                                            metricKey,
                                                            value
                                                        )}`
                                                    }
                                                    color={
                                                        isPositive
                                                            ? "#198754"
                                                            : "#DF2A16"
                                                    }
                                                    fillColor={
                                                        isPositive
                                                            ? "#EEFFF7"
                                                            : "#FFF2EE"
                                                    }
                                                    showDates={
                                                        Array.isArray(
                                                            data[metricKey].series
                                                        ) &&
                                                        data[metricKey].series.length >
                                                        0 &&
                                                        data[metricKey].series[0] !==
                                                        null &&
                                                        typeof data[metricKey]
                                                            .series[0] === "object" &&
                                                        "date" in
                                                        (data[metricKey]
                                                            .series[0] as object)
                                                    }
                                                    dateFormatter={(
                                                        date: string | Date
                                                    ) => {
                                                        if (typeof date === "string") {
                                                            return new Date(
                                                                date
                                                            ).toLocaleDateString(
                                                                "en-US",
                                                                {
                                                                    month: "short",
                                                                    day: "numeric",
                                                                    year: "numeric",
                                                                }
                                                            );
                                                        }
                                                        return date.toLocaleDateString(
                                                            "en-US",
                                                            {
                                                                month: "short",
                                                                day: "numeric",
                                                                year: "numeric",
                                                            }
                                                        );
                                                    }}
                                                />
                                            )}
                                        </Col>
                                    </>
                                )}
                            </Row>
                        );
                    })}
                </>}
            </> : null}
            {
                !isComingSoon && config?.drilldown ?
                    <div className="laggards-wrapper" style={{ width: "100%" }}>
                        <RevstackLaggardsSectionWidget
                            drilldownData={drilldownData}
                            filterOptions={config?.filterOptions}
                            selectedFilter={selectedFilter}
                            setSelectedFilter={setSelectedFilter}
                            drilldown={config?.drilldown}
                            isLoading={isDrilldownLoading}
                            size={enableMetricAndGraph !== false ? "small" : "large"}
                            noDataText={config?.drilldown?.noDataText}
                            maxRowsInGroup={config?.maxRowsInGroup}
                        />
                    </div> : null
            }
        </WidgetCard>
    );
};

export default RevstackSummaryGraphCard;
